/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import React, { useEffect, useRef, useMemo, useState, useCallback } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useSelector } from "react-redux"
import { cashFlowStructure } from "../../actions/cash-flow.action"
import MaterialSelect from "../../components/MaterialSelect"
import { formatNumber } from "../../utils/data" // Asegúrate de importar tu función

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const BudgetChart = ({
  title,
  selectedCoin,
  selectedStructure,
  setSelectedStructure,
  selectedRow,
  setSelectedRow,
  customStructure,
  requiresStructure = true,
  itemsPerPage = 10,
  scrollStep = 1
}) => {
  const cashFlow = useSelector((state) => state.cashFlow);
  const chartRef = useRef();
  const containerRef = useRef();
  const scrollThumbRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const coinData = cashFlow?.coins?.find(c => c.CodMonedaInternacional === selectedCoin);

  useEffect(() => {
    if (cashFlow.cashFlowEstructure?.length || !requiresStructure) {
      return;
    }
    cashFlowStructure();
  }, [requiresStructure])
  
  useEffect(() => {
    if (!cashFlow.cashFlowEstructure?.length || selectedStructure || !requiresStructure) {
      return;
    }
    setSelectedStructure(`${cashFlow.cashFlowEstructure[0].Nivel}`);
  }, [cashFlow.cashFlowEstructure])

  const budgetData = useMemo(() => {
    const rawData = cashFlow.cashFlowPcgItemsResume.map(i => ({
      id: i.id,
      label: i.description,
      amount: (selectedCoin === 'PEN' ? i.SaldoPEN : i.SaldoUSD),
      fullLabel: i.description,
    }));
    
    return [...rawData].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  }, [cashFlow.cashFlowPcgItemsResume, selectedCoin]);

  const filteredBudgetData = useMemo(() => {
    if (!searchTerm.trim()) {
      return budgetData;
    }
    
    const term = searchTerm.toLowerCase().trim();
    return budgetData.filter(item => 
      item.label.toLowerCase().includes(term) || 
      item.id.toString().includes(term)
    );
  }, [budgetData, searchTerm]);

  const currentData = useMemo(() => {
    return filteredBudgetData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBudgetData, startIndex, itemsPerPage]);

  const { minValue, maxValue } = useMemo(() => {
    if (currentData.length === 0) {
      return { minValue: 0, maxValue: 100 };
    }
    
    const amounts = currentData.map(item => item.amount);
    const minAmount = Math.min(...amounts);
    const maxAmount = Math.max(...amounts);
    
    let calculatedMin = minAmount;
    let calculatedMax = maxAmount;
    
    if (minAmount >= 0) {
      calculatedMin = 0;
      calculatedMax = maxAmount;
    } 
    else if (maxAmount <= 0) {
      calculatedMin = minAmount;
      calculatedMax = 0;
    }
    else {
      calculatedMin = minAmount;
      calculatedMax = maxAmount;
    }
    const margin = Math.max(Math.abs(maxAmount), Math.abs(minAmount)) * 0.05;
    return {
      minValue: Number(calculatedMin < 0 ? calculatedMin - margin : calculatedMin).toFixed(0),
      maxValue: Number(calculatedMax > 0 ? calculatedMax + margin : calculatedMax).toFixed(0),
    };
  }, [currentData]);

  const totalSteps = useMemo(() => {
    if (filteredBudgetData.length <= itemsPerPage) return 1;
    const availableItems = filteredBudgetData.length - itemsPerPage;
    return Math.ceil(availableItems / scrollStep) + 1;
  }, [filteredBudgetData.length, itemsPerPage, scrollStep]);

  const currentStep = useMemo(() => {
    if (totalSteps <= 1) return 0;
    const step = Math.floor(startIndex / scrollStep);
    return Math.min(step, totalSteps - 1);
  }, [startIndex, scrollStep, totalSteps]);

  const thumbHeight = useMemo(() => {
    if (totalSteps <= 1) return 100;
    return 100 / totalSteps;
  }, [totalSteps]);

  const thumbPosition = useMemo(() => {
    if (totalSteps <= 1) return 0;
    const maxPosition = 100 - thumbHeight;
    const position = (currentStep / (totalSteps - 1)) * maxPosition;
    return Math.min(position, maxPosition);
  }, [currentStep, totalSteps, thumbHeight]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current || totalSteps <= 1) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
    const percentage = (y / rect.height) * 100;
    const maxPosition = 100 - thumbHeight;
    const relativePosition = Math.max(0, Math.min(maxPosition, percentage));
    const stepPercentage = relativePosition / maxPosition;
    const newStep = Math.round(stepPercentage * (totalSteps - 1));
    
    const newStartIndex = Math.min(newStep * scrollStep, filteredBudgetData.length - itemsPerPage);
    setStartIndex(newStartIndex);
  }, [isDragging, totalSteps, scrollStep, filteredBudgetData.length, itemsPerPage, thumbHeight]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    setStartIndex(0);
  }, [searchTerm]);

  const barThickness = useMemo(() => {
    const baseHeight = 20;
    const minHeight = 15;
    const maxHeight = 30;
    
    if (currentData.length <= 5) return maxHeight;
    if (currentData.length <= 10) return baseHeight;
    return minHeight;
  }, [currentData.length]);

  const chartData = {
    labels: currentData.map(item => item.label),
    datasets: [
      {
        label: `Saldo`,
        data: currentData.map(item => item.amount),
        backgroundColor: currentData.map(item => 
          selectedRow === item.id ? "rgb(60, 141, 188)" : "#8A2BE2"
        ),
        borderColor: currentData.map(item => 
          selectedRow === item.id ? "rgb(40, 121, 168)" : "#6A1BB8"
        ),
        borderWidth: 1,
        barThickness: barThickness,
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 20,
        top: currentData.length < 5 ? 20 : 0,
        bottom: currentData.length < 5 ? 20 : 0
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `${title} \n (${coinData?.Descripcion})`,
        font: {
          size: 12,
          weight: 'bold'
        },
        color: '#666',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            // Usar formatNumber en lugar de toFixed(2)
            const formattedValue = formatNumber(context.raw);
            return `${formattedValue}`;
          },
          title: function(tooltipItems) {
            const item = currentData[tooltipItems[0].dataIndex];
            return item.fullLabel;
          },
          // Opcional: puedes agregar información adicional si lo necesitas
          afterLabel: function(context) {
            // Ejemplo: mostrar la moneda
            return `Moneda: ${selectedCoin}`;
          }
        },
        displayColors: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#ddd',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        min: minValue,
        max: maxValue,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 10,
          },
          // También formatear los ticks del eje X
          callback: function(value) {
            return formatNumber(value);
          }
        }
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
          },
          callback: function(value, index) {
            const label = currentData[index]?.label;
            if (label && label.length > 40) {
              return label.substring(0, 40) + '...';
            }
            return label;
          }
        }
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const item = currentData[index];
        handleRowClick(item.id);
      }
    },
    onHover: (event, elements) => {
      const chart = chartRef.current;
      if (chart) {
        chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },
  };

  const handleRowClick = (id) => {
    if (selectedRow === id) {
      setSelectedRow('');
    } else {
      setSelectedRow(id);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div style={{
      padding: '16px',
      width: "100%",
      height: "100%",
      backgroundColor: "white",
      borderRadius: "4px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flex: 1,
          minWidth: "300px"
        }}>
          {requiresStructure && (
            <div style={{ flexShrink: 0 }}>
              <MaterialSelect
                label=""
                value={selectedStructure}
                onChange={(e) => setSelectedStructure(e.target.value)}
                width="8em"
              >
                {cashFlow.cashFlowEstructure.map(e => (
                  <option key={e.Nivel} value={e.Nivel}>{e.Descripcion}</option>
                ))}
              </MaterialSelect>
            </div>
          )}
          
          {customStructure?.length && (
            <div style={{ flexShrink: 0 }}>
              <MaterialSelect
                label=""
                value={selectedStructure}
                onChange={(e) => setSelectedStructure(e.target.value)}
                width="8em"
              >
                {customStructure.map(e => (
                  <option key={e.Nivel} value={e.Nivel}>{e.Descripcion}</option>
                ))}
              </MaterialSelect>
            </div>
          )}

          <div style={{
            flex: 1,
            position: 'relative',
            maxWidth: '200px',
            height: '40px',
          }}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Buscar..."
              style={{
                width: '100%',
                height: '1.8em',
                padding: '4px 6px',
                paddingRight: '32px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgb(60, 141, 188)';
                e.target.style.boxShadow = '0 0 0 2px rgba(60, 141, 188, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ccc';
                e.target.style.boxShadow = 'none';
              }}
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '.8em',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0',
                  width: '20px',
                  height: '1em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ 
        flex: 1, 
        display: 'flex',
        width: '100%',
        minHeight: 0,
        position: 'relative'
      }}>
        <div style={{ 
          flex: 1,
          position: 'relative',
          minWidth: 0, 
          maxHeight: `${((itemsPerPage <= currentData.length ? itemsPerPage : (currentData.length + 1)) + 2) * 3}em`,
        }}>
          <Bar 
            ref={chartRef}
            data={chartData} 
            options={chartOptions}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </div>

        {totalSteps > 1 && (
          <div
            ref={containerRef}
            style={{
              width: '16px',
              height: '100%',
              marginLeft: '8px',
              backgroundColor: '#f0f0f0',
              borderRadius: '8px',
              position: 'relative',
              cursor: 'pointer',
              flexShrink: 0,
              minHeight: '200px'
            }}
            onClick={(e) => {
              if (totalSteps <= 1) return;
              
              const container = e.currentTarget;
              const rect = container.getBoundingClientRect();
              const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
              const percentage = (y / rect.height) * 100;
              const maxPosition = 100 - thumbHeight;
              const relativePosition = Math.max(0, Math.min(maxPosition, percentage));
              const stepPercentage = relativePosition / maxPosition;
              const newStep = Math.round(stepPercentage * (totalSteps - 1));
              
              const newStartIndex = Math.min(newStep * scrollStep, filteredBudgetData.length - itemsPerPage);
              setStartIndex(newStartIndex);
            }}
          >
            <div
              ref={scrollThumbRef}
              style={{
                position: 'absolute',
                top: `${thumbPosition}%`,
                width: '100%',
                height: `${thumbHeight}%`,
                backgroundColor: "rgb(60, 141, 188)",
                borderRadius: '8px',
                cursor: 'grab',
                transition: isDragging ? 'none' : 'top 0.2s ease',
                minHeight: '20px',
                border: '1px solid rgb(40, 121, 168)'
              }}
              onMouseDown={handleMouseDown}
            />
          </div>
        )}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#666',
        textAlign: 'center'
      }}>
        Items {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredBudgetData.length)} de {filteredBudgetData.length}
        {searchTerm && (
          <span style={{ marginLeft: '10px', color: 'rgb(60, 141, 188)' }}>
            (Filtrado: {filteredBudgetData.length} de {budgetData.length})
          </span>
        )}
      </div>
    </div>
  )
}

export default BudgetChart;